/**
 * Appearance: colour scheme (auto/light/dark → data-bs-theme) and colour theme (9 legacy themes →
 * data-creator-theme, see styles/themes.scss), both stored as preferences (color_scheme,
 * color_theme). public/color-mode.js applies the stored values before the first paint.
 */
import { computed, ref, watchEffect, type WritableComputedRef } from 'vue';
import { usePreferencesStore, type ColorScheme, type ColorTheme } from '../stores/preferences';

export type ColorMode = ColorScheme;

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : undefined;
const systemDark = ref(media?.matches ?? false);
media?.addEventListener('change', (e) => (systemDark.value = e.matches));

let applied = false;

/** Apply scheme + theme to <html> whenever the preferences change (call once at start). */
export function applyAppearance(): void {
  if (applied) return;
  applied = true;
  const prefs = usePreferencesStore();
  watchEffect(() => {
    const mode = prefs.values.color_scheme;
    const dark = mode === 'dark' || (mode === 'auto' && systemDark.value);
    const root = document.documentElement;
    root.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
    root.setAttribute('data-creator-theme', prefs.values.color_theme);
    root.setAttribute('data-creator-margin', prefs.values.theme_margin);
    // browser UI colour (address bar on mobile) follows the theme
    const accent = getComputedStyle(root).getPropertyValue('--creator-accent').trim();
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && accent) meta.setAttribute('content', dark ? '#1e2128' : accent);
  });
}

/** Colour scheme and theme as writable refs (backed by the preferences store). */
export function useColorMode(): { mode: WritableComputedRef<ColorMode>; theme: WritableComputedRef<ColorTheme>; dark: WritableComputedRef<boolean> } {
  const prefs = usePreferencesStore();
  const mode = computed<ColorMode>({ get: () => prefs.values.color_scheme, set: (v) => prefs.set('color_scheme', v) });
  const theme = computed<ColorTheme>({ get: () => prefs.values.color_theme, set: (v) => prefs.set('color_theme', v) });
  const dark = computed<boolean>({ get: () => mode.value === 'dark' || (mode.value === 'auto' && systemDark.value), set: (v) => (mode.value = v ? 'dark' : 'light') });
  return { mode, theme, dark };
}
